import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { format, formatDistanceToNow } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Bot, User, Loader2, RotateCcw, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type HistoryEntry = {
  _id: string;
  entityType: "templateSection" | "globalRule" | "documentSection";
  entityId: string;
  field: string;
  oldValue: string;
  newValue: string;
  summary: string;
  source: "user" | "agent";
  agentId?: string;
  agentName?: string;
  chatId?: string;
  createdAt: number;
};

function docSectionEntityId(
  sessionId: string,
  sectionTitle: string,
): string {
  return `${sessionId}::${sectionTitle.trim().toLowerCase()}`;
}

function preview(text: string, n = 240): string {
  if (!text) return "(empty)";
  const single = text.replace(/\s+/g, " ").trim();
  return single.length > n ? single.slice(0, n - 1) + "…" : single;
}

export function DocumentSectionHistoryPanel({
  open,
  onOpenChange,
  userId,
  sessionId,
  sectionTitle,
  currentContent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: Id<"users">;
  sessionId: Id<"recordingSessions">;
  sectionTitle: string;
  currentContent: string;
}) {
  const { toast } = useToast();
  const restoreSectionVersion = useMutation(
    api.recordingSessions.restoreSectionVersion,
  );
  const entityId = docSectionEntityId(String(sessionId), sectionTitle);
  const result = useQuery(
    api.editHistory.listForEntity,
    open
      ? {
          userId,
          entityType: "documentSection" as const,
          entityId,
          limit: 50,
        }
      : "skip",
  );
  const entries = (result as HistoryEntry[] | undefined) || undefined;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const selected = entries?.find((e) => e._id === selectedId) || null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[520px] sm:max-w-[520px] flex flex-col p-0"
        data-testid="sheet-section-history"
      >
        <SheetHeader className="p-4 border-b border-border shrink-0">
          <SheetTitle>Section history</SheetTitle>
          <SheetDescription>
            Previous versions of “{sectionTitle}”. Restore any version to
            replace the current content — the restore is itself saved as a new
            entry so you can undo it.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 space-y-1">
              {entries === undefined ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : entries.length === 0 ? (
                <p
                  className="text-sm text-muted-foreground text-center py-8"
                  data-testid="text-section-history-empty"
                >
                  No edits yet for this section.
                </p>
              ) : (
                entries.map((entry) => {
                  const isSelected = selectedId === entry._id;
                  const createdAtDate = new Date(entry.createdAt);
                  const relativeTime = formatDistanceToNow(createdAtDate, {
                    addSuffix: true,
                  });
                  const absoluteTime = format(
                    createdAtDate,
                    "MMM d, yyyy 'at' h:mm a",
                  );
                  return (
                    <button
                      key={entry._id}
                      type="button"
                      onClick={() =>
                        setSelectedId(isSelected ? null : entry._id)
                      }
                      className={`w-full text-left p-3 rounded-md border transition-colors ${
                        isSelected
                          ? "border-primary/50 bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                      data-testid={`section-history-entry-${entry._id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground"
                                data-testid={`section-history-entry-time-${entry._id}`}
                              >
                                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                {relativeTime}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {absoluteTime}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Badge
                          variant={
                            entry.source === "agent" ? "default" : "secondary"
                          }
                          className="text-[10px] gap-1 shrink-0"
                        >
                          {entry.source === "agent" ? (
                            <>
                              <Bot className="w-3 h-3" />
                              {entry.agentName || "Agent"}
                            </>
                          ) : (
                            <>
                              <User className="w-3 h-3" />
                              You
                            </>
                          )}
                        </Badge>
                      </div>
                      <div
                        className="text-xs text-muted-foreground mb-1"
                        data-testid={`section-history-entry-absolute-time-${entry._id}`}
                      >
                        {absoluteTime}
                      </div>
                      <div className="text-xs text-foreground/80 line-clamp-1">
                        {entry.summary}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                        {preview(entry.oldValue, 160)}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {selected && (
            <div className="border-t border-border bg-muted/20 p-3 max-h-[55%] overflow-auto shrink-0">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold">
                  Previous version preview
                </div>
                {(() => {
                  const target = selected.oldValue ?? "";
                  const matchesCurrent = currentContent === target;
                  const disabled =
                    restoringId === selected._id || matchesCurrent;
                  return (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      data-testid={`button-restore-section-${selected._id}`}
                      disabled={disabled}
                      title={
                        matchesCurrent
                          ? "Section content already matches that version"
                          : undefined
                      }
                      onClick={async () => {
                        const ok = window.confirm(
                          `Restore “${sectionTitle}” to this previous version? The current content will be replaced and a new history entry recorded.`,
                        );
                        if (!ok) return;
                        setRestoringId(selected._id);
                        try {
                          await restoreSectionVersion({
                            id: sessionId,
                            userId,
                            sectionTitle,
                            historyEntryId:
                              selected._id as Id<"editHistory">,
                          });
                          toast({
                            title: "Section restored",
                            description:
                              "A new history entry was added so you can undo this too.",
                          });
                          setSelectedId(null);
                          onOpenChange(false);
                        } catch (err: unknown) {
                          const message =
                            err instanceof Error
                              ? err.message
                              : "Something went wrong restoring this version.";
                          toast({
                            title: "Couldn't restore",
                            description: message,
                            variant: "destructive",
                          });
                        } finally {
                          setRestoringId(null);
                        }
                      }}
                    >
                      {restoringId === selected._id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3 h-3" />
                      )}
                      Restore this version
                    </Button>
                  );
                })()}
              </div>
              <pre
                className="whitespace-pre-wrap font-mono text-xs bg-background border border-border rounded p-2 max-h-72 overflow-auto"
                data-testid="section-history-preview"
              >
                {selected.oldValue || "(empty)"}
              </pre>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
